import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Trophy,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { dataStore } from "@/lib/store";
import { useSupabase } from "@/hooks/useSupabase";
import { Athlete, AthleteScore } from "@shared/types";

interface RankingEntry {
  position: number;
  athlete: Athlete;
  score: AthleteScore;
}

type RankingType = "11:00" | "14:30" | "geral";

interface RankingData {
  "11:00": {
    Masculino: RankingEntry[];
    Feminino: RankingEntry[];
  };
  "14:30": {
    Masculino: RankingEntry[];
    Feminino: RankingEntry[];
  };
  geral: {
    Masculino: RankingEntry[];
    Feminino: RankingEntry[];
  };
}

export default function Ranking() {
  const { store } = useSupabase();
  const [rankings, setRankings] = useState<RankingData>({
    "11:00": { Masculino: [], Feminino: [] },
    "14:30": { Masculino: [], Feminino: [] },
    geral: { Masculino: [], Feminino: [] },
  });
  const [currentRankingType, setCurrentRankingType] =
    useState<RankingType>("geral");
  const [currentGender, setCurrentGender] = useState<"Masculino" | "Feminino">(
    "Masculino",
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const [isLoadingRankings, setIsLoadingRankings] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [isRealTimeConnected, setIsRealTimeConnected] = useState(false);

  const ATHLETES_PER_PAGE = 5;
  const FULLSCREEN_ATHLETES_PER_PAGE = 5;
  const AUTO_ROTATION_INTERVAL = 5000; // 5 seconds

  useEffect(() => {
    loadRankings();

    // Set up real-time subscription for instant updates (if using Supabase)
    let unsubscribe: (() => void) | null = null;
    if (store && "onRealtimeUpdate" in store) {
      console.log("🔄 Setting up real-time ranking updates...");
      setIsRealTimeConnected(true);
      unsubscribe = (store as any).onRealtimeUpdate(() => {
        console.log("⚡ Real-time update detected, refreshing rankings...");
        setLastUpdateTime(new Date());
        loadRankings();
      });
    } else {
      setIsRealTimeConnected(false);
    }

    // Auto-refresh rankings every 30 seconds (fallback)
    const refreshInterval = setInterval(() => {
      console.log("🔄 Auto-refresh ranking (30s interval)...");
      loadRankings();
    }, 30000);

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Listen for escape key to exit fullscreen
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      if (unsubscribe) {
        console.log("🔌 Unsubscribing from real-time updates");
        unsubscribe();
      }
      clearInterval(refreshInterval);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [store, isFullscreen]);

  // Reset page when entering fullscreen mode if current page is invalid
  useEffect(() => {
    const totalPages = getTotalPages();
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(0);
    }
  }, [isFullscreen, currentPage, currentRankingType, currentGender]);

  // Verificação inicial: se iniciar auto-rotação em ranking vazio, mudar para um com atletas
  useEffect(() => {
    if (isAutoRotating && !hasAthletes(currentRankingType, currentGender)) {
      // Tentar encontrar um ranking com atletas para começar a rotação
      if (hasAthletes(currentRankingType, "Masculino")) {
        setCurrentGender("Masculino");
      } else if (hasAthletes(currentRankingType, "Feminino")) {
        setCurrentGender("Feminino");
      }
      // Se nenhum gender do ranking atual tem atletas, a rotação vai ficar parada
      // até que o usuário mude manualmente para um ranking com atletas
    }
  }, [isAutoRotating, currentRankingType, currentGender, rankings]);

  useEffect(() => {
    let rotationInterval: NodeJS.Timeout;

    if (isAutoRotating) {
      rotationInterval = setInterval(() => {
        const currentRanking = rankings[currentRankingType][currentGender];
        const athletesPerPage = isFullscreen
          ? FULLSCREEN_ATHLETES_PER_PAGE
          : ATHLETES_PER_PAGE;
        const totalPages = Math.ceil(currentRanking.length / athletesPerPage);

        if (currentPage < totalPages - 1) {
          // Go to next page within current ranking
          setCurrentPage((prev) => prev + 1);
        } else if (currentGender === "Masculino") {
          // Check if female ranking has athletes before switching
          if (hasAthletes(currentRankingType, "Feminino")) {
            setCurrentGender("Feminino");
            setCurrentPage(0);
          } else {
            // Skip empty female ranking, stay on male
            setCurrentPage(0);
          }
        } else {
          // Switch back to male of same ranking type
          // Male should always exist if we're rotating, but check for safety
          if (hasAthletes(currentRankingType, "Masculino")) {
            setCurrentGender("Masculino");
            setCurrentPage(0);
          } else {
            // If male is also empty, stay on current
            setCurrentPage(0);
          }
        }
      }, AUTO_ROTATION_INTERVAL);
    }

    return () => {
      if (rotationInterval) clearInterval(rotationInterval);
    };
  }, [
    isAutoRotating,
    currentPage,
    currentGender,
    currentRankingType,
    rankings,
    isFullscreen,
  ]);

  const loadRankings = async () => {
    const startTime = performance.now();
    console.log("🏆 Loading all rankings (general + time slots)...");

    setIsLoadingRankings(true);
    try {
      console.log("👨 Fetching male rankings...");
      const maleStartTime = performance.now();
      const maleData = await store.getRanking("Masculino");
      console.log(
        `👨 Male rankings loaded in ${(performance.now() - maleStartTime).toFixed(2)}ms (${maleData.length} athletes)`,
      );

      console.log("👩 Fetching female rankings...");
      const femaleStartTime = performance.now();
      const femaleData = await store.getRanking("Feminino");
      console.log(
        `👩 Female rankings loaded in ${(performance.now() - femaleStartTime).toFixed(2)}ms (${femaleData.length} athletes)`,
      );

      // Helper function to create ranking entries with positions
      const createRankingEntries = (data: typeof maleData) =>
        data.map((entry, index) => ({
          position: index + 1,
          athlete: entry.athlete,
          score: entry.score,
        }));

      // Filter and separate rankings by time slot
      const male1100 = maleData.filter(
        (entry) => entry.athlete.timeSlot === "11:00",
      );
      const male1430 = maleData.filter(
        (entry) => entry.athlete.timeSlot === "14:30",
      );
      const female1100 = femaleData.filter(
        (entry) => entry.athlete.timeSlot === "11:00",
      );
      const female1430 = femaleData.filter(
        (entry) => entry.athlete.timeSlot === "14:30",
      );

      // Update rankings state
      setRankings({
        "11:00": {
          Masculino: createRankingEntries(male1100),
          Feminino: createRankingEntries(female1100),
        },
        "14:30": {
          Masculino: createRankingEntries(male1430),
          Feminino: createRankingEntries(female1430),
        },
        geral: {
          Masculino: createRankingEntries(maleData),
          Feminino: createRankingEntries(femaleData),
        },
      });

      const totalTime = (performance.now() - startTime).toFixed(2);
      console.log(`🏆 All rankings loaded in ${totalTime}ms`);
      console.log(
        `📊 Distribution - 11:00: M${male1100.length}/F${female1100.length}, 14:30: M${male1430.length}/F${female1430.length}, Geral: M${maleData.length}/F${femaleData.length}`,
      );
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error("❌ Error loading rankings:", error);
    } finally {
      setIsLoadingRankings(false);
    }
  };

  const isFullscreenSupported = () => {
    // Always return true to enable the button - we'll handle fallbacks in toggleFullscreen
    return true;
  };

  const toggleFullscreen = async () => {
    setFullscreenError(null);

    // Try native fullscreen first
    const hasNativeFullscreen = !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );

    if (hasNativeFullscreen) {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
          setIsFullscreen(true);
        } else {
          await document.exitFullscreen();
          setIsFullscreen(false);
        }
        return; // Success with native fullscreen
      } catch (error: any) {
        console.error("Native fullscreen failed:", error);
        // Fall through to CSS fallback
      }
    }

    // CSS fallback for environments where native fullscreen doesn't work
    console.log("Using CSS fallback for fullscreen mode");
    setIsFullscreen(!isFullscreen);
  };

  const getCurrentPageData = () => {
    const currentRanking = rankings[currentRankingType][currentGender];
    const athletesPerPage = isFullscreen
      ? FULLSCREEN_ATHLETES_PER_PAGE
      : ATHLETES_PER_PAGE;
    const startIndex = currentPage * athletesPerPage;
    return currentRanking.slice(startIndex, startIndex + athletesPerPage);
  };

  const getTotalPages = () => {
    const currentRanking = rankings[currentRankingType][currentGender];
    const athletesPerPage = isFullscreen
      ? FULLSCREEN_ATHLETES_PER_PAGE
      : ATHLETES_PER_PAGE;
    return Math.ceil(currentRanking.length / athletesPerPage);
  };

  const goToNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const switchGender = (gender: "Masculino" | "Feminino") => {
    setCurrentGender(gender);
    setCurrentPage(0);
  };

  const switchRankingType = (rankingType: RankingType) => {
    setCurrentRankingType(rankingType);
    setCurrentPage(0);
  };

  const getRankingTypeLabel = (type: RankingType) => {
    switch (type) {
      case "11:00":
        return "Bateria 11:00";
      case "14:30":
        return "Bateria 14:30";
      case "geral":
        return "Ranking Geral";
      default:
        return "Ranking";
    }
  };

  const hasAthletes = (
    rankingType: RankingType,
    gender: "Masculino" | "Feminino",
  ) => {
    return rankings[rankingType][gender].length > 0;
  };

  const currentPageData = getCurrentPageData();
  const totalPages = getTotalPages();

  if (isFullscreen) {
    return (
      <div
        className={`${document.fullscreenElement ? "h-screen" : "fixed inset-0 z-50"} bg-white flex flex-col overflow-hidden`}
      >
        {/* Fullscreen Header */}
        <header
          className="bg-primary text-white flex-shrink-0"
          style={{ height: "calc(12vh)" }}
        >
          <div className="container mx-auto px-6 h-full flex items-center">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-4">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F525330fe20684a269584d84d399c39f6%2Fe804dffb024540e5b053023d69334fd2?format=webp&width=800"
                  alt="Boulder Academia Logo"
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h1 className="text-2xl font-bold">Festival de Escalada</h1>
                  <p className="text-blue-100 text-sm">
                    Boulder Academia - Ranking Oficial
                  </p>
                </div>
              </div>
              <Button
                onClick={toggleFullscreen}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 text-sm px-3 py-1"
              >
                ✕ Sair do Telão
              </Button>
            </div>
          </div>
        </header>

        {/* Fullscreen Content */}
        <main
          className="flex-1 flex flex-col min-h-0 container mx-auto px-6"
          style={{ height: "calc(88vh)" }}
        >
          <div
            className="flex justify-between items-center flex-shrink-0"
            style={{ height: "calc(8vh)", minHeight: "50px" }}
          >
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900">
                {getRankingTypeLabel(currentRankingType)} - {currentGender}
              </h2>
              <Badge variant="outline" className="text-sm px-3 py-1 bg-white">
                {currentPage + 1}/{totalPages}
              </Badge>

              <div className="flex items-center space-x-2">
                <div
                  className={`w-2 h-2 rounded-full ${isRealTimeConnected ? "bg-green-500" : "bg-red-500"}`}
                />
                <span className="text-sm text-gray-700">
                  {isRealTimeConnected ? "Real-time" : "Manual"}
                </span>
                {lastUpdateTime && (
                  <span className="text-xs text-gray-600">
                    • {lastUpdateTime.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                <Button
                  onClick={() => switchRankingType("geral")}
                  variant={
                    currentRankingType === "geral" ? "default" : "outline"
                  }
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  Geral
                </Button>
                <Button
                  onClick={() => switchRankingType("11:00")}
                  variant={
                    currentRankingType === "11:00" ? "default" : "outline"
                  }
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  11:00
                </Button>
                <Button
                  onClick={() => switchRankingType("14:30")}
                  variant={
                    currentRankingType === "14:30" ? "default" : "outline"
                  }
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  14:30
                </Button>
              </div>
              <div className="flex space-x-1">
                <Button
                  onClick={() => switchGender("Masculino")}
                  variant={
                    currentGender === "Masculino" ? "default" : "outline"
                  }
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  M
                </Button>
                <Button
                  onClick={() => switchGender("Feminino")}
                  variant={currentGender === "Feminino" ? "default" : "outline"}
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  F
                </Button>
              </div>
              <Button
                onClick={() => setIsAutoRotating(!isAutoRotating)}
                variant={isAutoRotating ? "default" : "outline"}
                size="sm"
                className="text-xs px-3 py-1"
              >
                {isAutoRotating ? (
                  <Pause className="w-3 h-3 mr-1" />
                ) : (
                  <Play className="w-3 h-3 mr-1" />
                )}
                {isAutoRotating ? "Pausar" : "Auto"}
              </Button>
              <div className="flex space-x-1">
                <Button
                  onClick={goToPrevPage}
                  disabled={currentPage === 0}
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages - 1}
                  size="sm"
                  className="text-xs px-2 py-1"
                >
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>

          {/* Rankings Display */}
          <div
            className="flex-1 flex flex-col overflow-hidden"
            style={{ height: "calc(80vh)", gap: "calc(1vh)" }}
          >
            {isLoadingRankings ? (
              // Loading skeleton for fullscreen
              Array.from({ length: 5 }).map((_, index) => (
                <Card
                  key={index}
                  className="border-2 shadow-md flex-shrink-0 flex items-center"
                  style={{ height: "calc(15vh)", padding: "calc(1vh)" }}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center space-x-4">
                      <div
                        className="rounded-full bg-gray-200 animate-pulse"
                        style={{ width: "calc(8vh)", height: "calc(8vh)" }}
                      ></div>
                      <div>
                        <div
                          className="bg-gray-200 rounded animate-pulse mb-2"
                          style={{ height: "calc(4vh)", width: "calc(25vw)" }}
                        ></div>
                        <div
                          className="bg-gray-200 rounded animate-pulse"
                          style={{ height: "calc(2vh)", width: "calc(12vw)" }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="bg-gray-200 rounded animate-pulse mb-2"
                        style={{ height: "calc(6vh)", width: "calc(8vw)" }}
                      ></div>
                      <div
                        className="bg-gray-200 rounded animate-pulse"
                        style={{ height: "calc(2vh)", width: "calc(6vw)" }}
                      ></div>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <>
                {currentPageData.map((entry) => (
                  <Card
                    key={entry.athlete.id}
                    className="border-2 shadow-md flex-shrink-0 flex items-center"
                    style={{ height: "calc(15vh)", padding: "calc(1vh)" }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-4">
                        <div
                          className={`rounded-full flex items-center justify-center font-bold shadow-lg ${
                            entry.position === 1
                              ? "bg-yellow-500 text-white"
                              : entry.position === 2
                                ? "bg-gray-400 text-white"
                                : entry.position === 3
                                  ? "bg-amber-600 text-white"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                          style={{
                            width: "calc(8vh)",
                            height: "calc(8vh)",
                            fontSize: "calc(2vh)",
                          }}
                        >
                          {entry.position}°
                        </div>
                        <div>
                          <h3
                            className="font-bold text-gray-900"
                            style={{
                              fontSize: "calc(3.5vh)",
                              lineHeight: "1.1",
                            }}
                          >
                            {entry.athlete.name}
                          </h3>
                          <p
                            className="text-gray-600"
                            style={{ fontSize: "calc(2vh)", lineHeight: "1.2" }}
                          >
                            Bateria {entry.athlete.timeSlot}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className="font-bold text-primary"
                          style={{ fontSize: "calc(5vh)", lineHeight: "1" }}
                        >
                          {entry.score.totalScore}
                        </div>
                        <p
                          className="text-gray-600"
                          style={{ fontSize: "calc(2vh)", lineHeight: "1.2" }}
                        >
                          pontos
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </>
            )}
          </div>

          {currentPageData.length === 0 && !isLoadingRankings && (
            <div
              className="text-center flex-shrink-0"
              style={{
                height: "calc(10vh)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div>
                <p style={{ fontSize: "calc(3vh)" }} className="text-gray-500">
                  Nenhum atleta cadastrado nesta categoria
                </p>
                {isAutoRotating && (
                  <p
                    style={{ fontSize: "calc(2vh)" }}
                    className="text-gray-400 mt-2"
                  >
                    A rotação automática vai pular esta categoria
                  </p>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-primary text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Ranking do Festival</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Controles do Ranking</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex space-x-2">
                <Button
                  onClick={() => switchRankingType("geral")}
                  variant={
                    currentRankingType === "geral" ? "default" : "outline"
                  }
                  disabled={isLoadingRankings}
                >
                  Ranking Geral
                </Button>
                <Button
                  onClick={() => switchRankingType("11:00")}
                  variant={
                    currentRankingType === "11:00" ? "default" : "outline"
                  }
                  disabled={isLoadingRankings}
                >
                  Bateria 11:00
                </Button>
                <Button
                  onClick={() => switchRankingType("14:30")}
                  variant={
                    currentRankingType === "14:30" ? "default" : "outline"
                  }
                  disabled={isLoadingRankings}
                >
                  Bateria 14:30
                </Button>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={() => switchGender("Masculino")}
                  variant={
                    currentGender === "Masculino" ? "default" : "outline"
                  }
                  disabled={isLoadingRankings}
                >
                  Masculino (
                  {isLoadingRankings
                    ? "..."
                    : rankings[currentRankingType].Masculino.length}
                  )
                </Button>
                <Button
                  onClick={() => switchGender("Feminino")}
                  variant={currentGender === "Feminino" ? "default" : "outline"}
                  disabled={isLoadingRankings}
                >
                  Feminino (
                  {isLoadingRankings
                    ? "..."
                    : rankings[currentRankingType].Feminino.length}
                  )
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  onClick={goToPrevPage}
                  disabled={currentPage === 0}
                  variant="outline"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 bg-gray-100 rounded">
                  {currentPage + 1} / {totalPages}
                </span>
                <Button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages - 1}
                  variant="outline"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <Button
                onClick={() => setIsAutoRotating(!isAutoRotating)}
                variant={isAutoRotating ? "default" : "outline"}
              >
                {isAutoRotating ? (
                  <Pause className="w-4 h-4 mr-2" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {isAutoRotating ? "Pausar Rotação" : "Rotação Automática"}
              </Button>

              <Button
                onClick={toggleFullscreen}
                className="ml-auto"
                title="Ativar modo tela cheia para apresentação"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Modo Telão
              </Button>

              <div className="flex items-center space-x-2">
                <Button
                  onClick={loadRankings}
                  variant="outline"
                  disabled={isLoadingRankings}
                >
                  {isLoadingRankings ? (
                    <>
                      <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Carregando...
                    </>
                  ) : (
                    "Atualizar"
                  )}
                </Button>

                <div className="flex items-center space-x-2 text-sm">
                  <div
                    className={`w-2 h-2 rounded-full ${isRealTimeConnected ? "bg-green-500" : "bg-red-500"}`}
                  />
                  <span className="text-gray-600">
                    {isRealTimeConnected ? "Tempo Real" : "Manual"}
                  </span>
                </div>

                {lastUpdateTime && (
                  <div className="text-xs text-gray-500">
                    Atualizado: {lastUpdateTime.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fullscreen Error */}
        {fullscreenError && (
          <Alert className="mb-8">
            <AlertDescription>{fullscreenError}</AlertDescription>
          </Alert>
        )}

        {/* Rankings Preview */}
        <Card>
          <CardHeader>
            <CardTitle>
              {getRankingTypeLabel(currentRankingType)} - {currentGender} -
              Página {currentPage + 1}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {isLoadingRankings ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse"></div>
                      <div>
                        <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-32"></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-20"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="h-8 bg-gray-200 rounded animate-pulse w-16 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-12"></div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {currentPageData.map((entry) => (
                    <div
                      key={entry.athlete.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            entry.position === 1
                              ? "bg-yellow-500 text-white"
                              : entry.position === 2
                                ? "bg-gray-400 text-white"
                                : entry.position === 3
                                  ? "bg-amber-600 text-white"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {entry.position}°
                        </div>
                        <div>
                          <h3 className="font-semibold">
                            {entry.athlete.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Bateria {entry.athlete.timeSlot}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          {entry.score.totalScore}
                        </div>
                        <p className="text-sm text-gray-600">pontos</p>
                      </div>
                    </div>
                  ))}

                  {currentPageData.length === 0 && !isLoadingRankings && (
                    <div className="text-center py-8 text-gray-500">
                      <p>Nenhum atleta cadastrado nesta categoria</p>
                      {isAutoRotating && (
                        <p className="text-sm text-gray-400 mt-2">
                          A rotação automática vai pular esta categoria
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
